/* eslint-disable react/destructuring-assignment */
import {
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  PauseOutlined,
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  Button,
  message,
  Modal,
  Spin,
  Switch,
  Tag,
  Segmented as ThreeWayToggle,
  Tooltip,
} from "antd";
import { capitalize } from "lodash";
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { MODIFY_EMAIL_CONSENT, MODIFY_LEAD_AUTO_RESPONDER } from "../../../graphql/mutation";
import useLang from "../../../hooks/useLang";
import { NewTabRedirect } from "../../../icons/js/NewTabRedirectionIcon";
import TickIcon from "../../../icons/js/TickIcon";
import { closeDialog, openDialog } from "../../../library/helpers";
import { setAutoResponseCampaign } from "../../../redux/slices/leadSlice";

const LeadConsent = props => {
  const { handleOptedOut, selectedLead, handleSendConsentClick, user, consentLoader } = props;

  const [lang] = useLang();
  const [selectedConsent, setselectedConsent] = useState("sms");

  const smsConsent = selectedLead?.textConsentStatus;
  const emailConsent = selectedLead?.emailConsent || null;

  const status = user?.company?.isOptinConsentMethod;
  const colorCodes = (consent, status) => {
    if (consent === "ACCEPTED") {
      return {
        bg: "bg-[#ECFDF5]",
        border: "border-[#10B981]",
        text: "text-[#10b981]",
        consent: "Implied Consent",
        icon: <TickIcon />,
      };
    }
    if (consent === "PENDING" && status) {
      return {
        bg: "bg-[#dbd0ae]",
        border: "border-[#ffbf00]",
        text: "text-[#ffbf00]",
        consent: "Pending",
        icon: <InfoCircleFilled />,
      };
    }
    if (consent === "PENDING" && !status) {
      return {
        bg: "bg-[#ECFDF5]",
        border: "border-[#10B981]",
        text: "text-[#10b981]",
        consent: "Implied Consent",
        icon: <TickIcon />,
      };
    }
    if (consent === "DECLINED") {
      return {
        bg: "bg-[#FEF2F2]",
        border: "border-[#EF4444]",
        text: "text-[#EF4444]",
        consent: "Opted-Out",
        icon: <CloseCircleFilled className="text-[#EF4444] text-[20px]" />,
      };
    }
    return {
      bg: "bg-gray-50",
      border: "border-gray-500",
      text: "text-gray-500",
      consent: "Send Consent",
      icon: <InfoCircleFilled />,
    };
  };
  const consentDataSMS = useMemo(() => colorCodes(smsConsent, status), [smsConsent, status]);
  const [modifyEmailConsent, { loading: emailConsentLoader }] = useMutation(MODIFY_EMAIL_CONSENT);
  const handleEmailConsent = async () => {
    const res = await modifyEmailConsent({
      variables: {
        leadId: selectedLead.id,
      },
    });
    if (res.data.modifyEmailConsent === 200) {
      message.success(res.data.modifyEmailConsent.message);
      closeDialog("confirmDialog");
    } else {
      message.error(res.data.modifyEmailConsent.message);
    }
  };

  return (
    <div className="flex-col-centered gap-1 items-start">
      <div className="flex-row-centered mb-1 items-center gap-1">
        <Tag
          onClick={() => setselectedConsent("sms")}
          className={`${
            selectedConsent === "sms" ? "consent-tag-selected" : "consent-tag"
          } cursor-pointer`}
        >
          SMS Consents
        </Tag>
        <Tag
          className={`${
            selectedConsent === "email" ? "consent-tag-selected" : "consent-tag"
          } cursor-pointer`}
        >
          Email Consents
        </Tag>
      </div>
      {selectedConsent === "sms" ? (
        <div className="grid grid-cols-2 flex-row-centered">
          {consentLoader ? (
            <Spin spinning tip="Sending Consent request..." />
          ) : (
            <Tag
              className={`h-full flex-col-centered cursor-pointer items-center justify-center ${consentDataSMS?.text} gap-1 ${consentDataSMS?.bg} ${consentDataSMS?.border} `}
              onClick={e => {
                if (smsConsent !== "DECLINED") {
                  handleSendConsentClick(e, selectedLead?.textConsentStatus);
                } else {
                  return message.error(`${lang.smsOptedOutErrorOnConsentRequest}`);
                }
              }}
            >
              {consentDataSMS?.icon}
              {consentDataSMS?.consent}
            </Tag>
          )}
          <ConsentJSX
            handleOptedOut={handleOptedOut}
            selectedConsent={selectedConsent}
            status={status}
            consent={smsConsent !== "DECLINED"}
            selectedLead={selectedLead}
            lang={lang}
            user={user}
            sendConsent={e => handleSendConsentClick(e, selectedLead?.textConsentStatus)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 flex-row-centered">
          <Tag
            className={`h-full flex-col-centered cursor-pointer items-center justify-center ${consentDataSMS?.text} gap-1 ${consentDataSMS?.bg} ${consentDataSMS?.border} `}
          >
            {consentDataSMS?.icon}
            {consentDataSMS?.consent}
          </Tag>
          <ConsentJSX
            handleOptedOut={handleEmailConsent}
            selectedConsent={selectedConsent}
            status={status}
            selectedLead={selectedLead}
            consent={emailConsent}
            lang={lang}
            user={user}
            // TODO: Add the consent handler for email
            sendConsent={e => console.log(e)}
          />
        </div>
      )}{" "}
      <Button
        type="primary"
        onClick={() =>
          openDialog("campaignToggle", {
            dialogHeader: "Turn off campaign message",
            selectedLead,
          })
        }
      >
        Activate/Deactivate Otto Campaign <NewTabRedirect />{" "}
      </Button>
    </div>
  );
};

export default LeadConsent;
function ConsentJSX({
  selectedConsent,
  selectedLead,
  lang,
  user,
  consent,
  handleOptedOut,
  sendConsent,
}) {
  const dispatch = useDispatch();
  const [ottoResponderState, setottoResponderState] = useState(
    selectedLead?.campaignAutoResponse || "DISENROLLED"
  );
  useEffect(() => {
    setottoResponderState(selectedLead?.campaignAutoResponse ?? "DISENROLLED");
  }, [selectedLead?.campaignAutoResponse]);
  const settings = useMemo(() => {
    if (selectedLead?.location?.settings) {
      return JSON.parse(selectedLead?.location?.settings);
    }
  }, [selectedLead]);
  const [modifyLeadAutoResponder] = useMutation(MODIFY_LEAD_AUTO_RESPONDER);
  const handleAutoResponse = async value => {
    const status = value;
    const res = await modifyLeadAutoResponder({
      variables: {
        leadId: selectedLead.id,
        status,
      },
    });
    if (res.data.modifyLeadAutoResponder.statusCode === 200) {
      message.success(res.data.modifyLeadAutoResponder.message);
      dispatch(setAutoResponseCampaign(status));
      setottoResponderState(value);
      closeDialog("confirmDialog");
    } else {
      message.error(res.data.modifyLeadAutoResponder.message);
    }
  };
  return (
    <div className="flex-col-centered gap-3">
      <div className="flex-row-centered gap-1 items-center text-sm text-gray-700">
        <Switch
          disabled={!consent}
          size="small"
          checked={consent}
          onChange={(checked, e) => {
            if (checked === false) {
              openDialog("confirmDialog", {
                label: "Are you sure you want to Opt-out this lead?",
                handleConfirm: handleOptedOut,
              });
            } else {
              sendConsent(e);
            }
          }}
        />{" "}
        <span className="flex-row-centered items-center gap-1">
          {selectedConsent.toUpperCase()} Status{" "}
          <Tooltip
            title={`This will stop you from sending any more ${capitalize(
              selectedConsent
            )} messages to the customer, including the automated messages (appointments, reminders, campaigns).`}
          >
            <InfoCircleOutlined className="w-3 h-3" />{" "}
          </Tooltip>
        </span>
      </div>
      <div className="flex-row-centered gap-1 items-center text-sm text-gray-700">
        <Switch size="small" />
        <span className="flex-row-centered items-center gap-1">
          {selectedConsent === "sms" ? lang.ottoNudgeToggle : "Otto Bot"}{" "}
          <Tooltip title={lang.ottoNudgeToggleTooltip}>
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </span>
      </div>
      <div className="flex-col-centered gap-1 items-start text-sm text-gray-700">
        <span className="flex-row-centered items-center gap-1">
          {lang.ottoResponderToggle}
          <Tooltip title="This will allow you to control the sending of automatic responses by the system.">
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </span>
        <ThreeWayToggle
          shape="round"
          value={ottoResponderState}
          onChange={value => {
            if (value === ottoResponderState) return;
            openDialog("confirmDialog", {
              label: `${
                value === "ENROLLED"
                  ? lang.startAutoResponderModalTitle
                  : value === "PAUSED"
                  ? lang.pauseAutoResponderModalTitle
                  : lang.stopAutoResponderModalTitle
              }`,
              content: `${
                value === "ENROLLED"
                  ? lang.startAutoResponderModalBody
                  : value === "PAUSED"
                  ? lang.pauseAutoResponderModalBody
                  : lang.stopAutoResponderModalBody
              }`,
              handleConfirm: () => handleAutoResponse(value),
            });
          }}
          disabled={!settings?.AUTO_RESPONSE_ENABLED ?? true}
          options={[
            {
              value: "ENROLLED",
              icon: (
                <Tooltip title="Enrolling the auto-responder will automatically send replies to customers.">
                  <CheckOutlined
                    className={`${ottoResponderState === "ENROLLED" ? "text-white" : ""}`}
                  />
                </Tooltip>
              ),
              className: ottoResponderState === "ENROLLED" ? "bg-brand-500" : "",
            },
            {
              value: "PAUSED",
              icon: (
                <Tooltip title="Pausing the auto-responder will temporarily stop sending automatic replies to customers.">
                  <PauseOutlined
                    className={`${ottoResponderState === "PAUSED" ? "text-white" : ""}`}
                  />
                </Tooltip>
              ),
              className: ottoResponderState === "PAUSED" ? "bg-yellow-500" : "",
            },
            {
              value: "DISENROLLED",
              icon: (
                <Tooltip title="Disenrolling the lead will remove them from the auto-responder campaign.">
                  <CloseOutlined
                    className={`${ottoResponderState === "DISENROLLED" ? "text-white" : ""}`}
                  />
                </Tooltip>
              ),
              className: ottoResponderState === "DISENROLLED" ? "bg-red-500" : "",
            },
          ]}
        />
        {capitalize(ottoResponderState)}
      </div>
      <div className="flex-row-centered gap-1 items-center text-sm text-gray-700">
        {/* <Switch
          onChange={checked => {
            if (!checked) {
              openDialog("campaignToggle", {
                dialogHeader: "Turn off campaign message",
                selectedLead,
              });
            }
          }}
          size="small"
        />
        <span className="flex-row-centered items-center gap-1">
          {lang.ottoCampaignToggle}{" "}
          <Tooltip title={lang.ottoCampaignToggleTooltip}>
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </span> */}
      </div>
    </div>
  );
}

/**
 *
 * @param {{open:boolean,setOpen:import("react").Dispatch<import("react").SetStateAction<boolean>>,title:string,okText:import("react").ReactNode,cancelText:import("react").ReactNode, onOk:()=>any,description:string,loader:boolean,okClassName:string}} props
 * @returns
 */
export const CancelScheduleConfirmation = props => {
  const handleCancel = () => props.setOpen(false);
  return (
    <Modal
      title={props.title}
      open={props.open}
      onCancel={handleCancel}
      footer={
        <div className="flex-row-centered justify-start items-center gap-2">
          <Button
            className={props.okClassName}
            loading={props.loader}
            type="primary"
            onClick={props.onOk}
          >
            {props.okText || "Yes"}
          </Button>
          <Button onClick={handleCancel}>{props.cancelText || "cancel"}</Button>
        </div>
      }
    >
      <span>{props.description}</span>
    </Modal>
  );
};
